import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  DatePicker, 
  TimePicker,
  Select, 
  message, 
  Space, 
  Tag, 
  Tooltip,
  Upload,
  Image,
  Descriptions
} from 'antd';
import { 
  PlusOutlined, 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  HomeOutlined,
  UploadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { useAuth } from '../../contexts/AuthContext';

const { TextArea } = Input;
const { Option } = Select;

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

const normalizeAttachments = (attachments) => {
  if (!attachments) return [];
  if (Array.isArray(attachments)) return attachments;

  if (typeof attachments === 'string') {
    try {
      const parsed = JSON.parse(attachments);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const getAttachmentName = (file = {}, index = 0) =>
  file.originalname || file.name || file.filename || `附件${index + 1}`;

const getAttachmentUrl = (file = {}) => {
  const rawPath = file.path || file.url || '';
  if (!rawPath) return '';
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
};

const isImageAttachment = (file = {}) => {
  const name = getAttachmentName(file).toLowerCase();
  const mimeType = String(file.mimetype || file.mimeType || '').toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => name.endsWith(`.${ext}`)) || mimeType.startsWith('image/');
};

const StudentHomeVisits = () => {
  const [visits, setVisits] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [viewingVisit, setViewingVisit] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchVisits();
    fetchClasses();
  }, []);

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v2/home-visits', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setVisits(response.data.data || []);
      }
    } catch (error) {
      console.error('获取家访记录失败:', error);
      message.error('获取家访记录失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async (classId = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (classId) {
        params.classId = classId;
      }
      const response = await axios.get('/api/v2/students', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (response.data.success) {
        setStudents(response.data.data || []);
      }
    } catch (error) {
      console.error('获取学生列表失败:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v2/classes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setClasses(response.data.data || []);
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
    }
  };

  const showAddModal = () => {
    setEditingVisit(null);
    setFileList([]);
    form.resetFields();
    form.setFieldsValue({
      visit_date: moment(),
    });
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingVisit(record);
    setFileList([]);
    form.setFieldsValue({
      student_id: record.student_id,
      visit_date: moment(record.visit_date),
      visit_time: record.visit_time ? moment(record.visit_time, 'HH:mm') : null,
      purpose: record.purpose,
      content: record.content,
      family_situation: record.family_situation,
      student_performance: record.student_performance,
      parent_feedback: record.parent_feedback,
      follow_up_plan: record.follow_up_plan
    });
    setModalVisible(true);
  };

  const showDetailModal = (record) => {
    setViewingVisit({
      ...record,
      attachments: JSON.stringify(normalizeAttachments(record.attachments))
    });
    setDetailModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('student_id', values.student_id);
      formData.append('visit_date', values.visit_date.format('YYYY-MM-DD'));
      if (values.visit_time) {
        formData.append('visit_time', values.visit_time.format('HH:mm'));
      }
      if (values.purpose) formData.append('purpose', values.purpose);
      formData.append('content', values.content);
      if (values.family_situation) formData.append('family_situation', values.family_situation);
      if (values.student_performance) formData.append('student_performance', values.student_performance);
      if (values.parent_feedback) formData.append('parent_feedback', values.parent_feedback);
      if (values.follow_up_plan) formData.append('follow_up_plan', values.follow_up_plan);

      // 添加附件
      fileList.forEach(file => {
        if (file.originFileObj) {
          formData.append('attachments', file.originFileObj);
        }
      });

      if (editingVisit) {
        formData.append('keep_old_attachments', 'true');
        await axios.put(`/api/v2/home-visits/${editingVisit.id}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        message.success('更新成功');
      } else {
        await axios.post('/api/v2/home-visits', formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        message.success('添加成功');
      }

      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      fetchVisits();
    } catch (error) {
      console.error('提交失败:', error);
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条家访记录吗？',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`/api/v2/home-visits/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          message.success('删除成功');
          fetchVisits();
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败');
        }
      }
    });
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过 10MB!');
        return false;
      }
      return false; // 阻止自动上传
    },
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    multiple: true,
    maxCount: 5
  };

/*  const renderAttachmentSummary = (attachments) => {
    const files = normalizeAttachments(attachments);
    return files.length > 0 ? `${files.length}个` : '-';
  };

*/
/*  const renderAttachmentSummary = (attachments) => {
    const files = normalizeAttachments(attachments);
    if (!files.length) {
      return '-';
    }
    return `${files.length}个`;
  };

*/
  const renderAttachmentCount = (attachments) => {
    const files = normalizeAttachments(attachments);
    return files.length ? String(files.length) : '-';
  };

  const renderAttachmentList = (attachments) => {
    const files = normalizeAttachments(attachments);
    if (!files.length) {
      return '-';
    }

    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {files.map((file, index) => {
          const url = getAttachmentUrl(file);
          const name = getAttachmentName(file, index);

          if (!url) {
            return <span key={`${name}-${index}`}>{name}</span>;
          }

          if (isImageAttachment(file)) {
            return (
              <Space key={`${name}-${index}`} align="start">
                <Image
                  src={url}
                  alt={name}
                  width={72}
                  height={72}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
                <a href={url} target="_blank" rel="noopener noreferrer">{name}</a>
              </Space>
            );
          }

          return (
            <Space key={`${name}-${index}`}>
              <a href={url} target="_blank" rel="noopener noreferrer">{name}</a>
              <Button type="link" size="small" icon={<DownloadOutlined />} href={url} target="_blank">
                打开
              </Button>
            </Space>
          );
        })}
      </Space>
    );
  };

  const columns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100,
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 120,
    },
    {
      title: '家访日期',
      dataIndex: 'visit_date',
      key: 'visit_date',
      width: 120,
      sorter: (a, b) => new Date(a.visit_date) - new Date(b.visit_date),
    },
    {
      title: '家访时间',
      dataIndex: 'visit_time',
      key: 'visit_time',
      width: 100,
    },
    {
      title: '家访目的',
      dataIndex: 'purpose',
      key: 'purpose',
      width: 150,
      ellipsis: true,
    },
    {
      title: '家访内容',
      dataIndex: 'content',
      key: 'content',
      width: 200,
      ellipsis: true,
    },
    {
      title: '教师',
      dataIndex: 'teacher_name',
      key: 'teacher_name',
      width: 100,
    },
    {
      title: '附件',
      dataIndex: 'attachments',
      key: 'attachments',
      width: 80,
      render: (attachments) => {
        if (!attachments) return '-';
        try {
          const files = JSON.parse(attachments);
          return files.length > 0 ? `${files.length}个` : '-';
        } catch {
          return '-';
        }
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => showDetailModal(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const normalizedColumns = columns.map((column) => (
    column.key === 'attachments'
      ? { ...column, render: renderAttachmentCount }
      : column
  ));

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h3>
          <HomeOutlined /> 家访管理
        </h3>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showAddModal}
        >
          添加家访记录
        </Button>
      </div>

      <Table
        columns={normalizedColumns}
        dataSource={visits}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
      />

      {/* 添加/编辑弹窗 */}
      <Modal
        title={editingVisit ? '编辑家访记录' : '添加家访记录'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setFileList([]);
        }}
        width={800}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="class_id"
            label="班级"
            rules={[{ required: true, message: '请先选择班级' }]}
          >
            <Select
              placeholder="请先选择班级"
              onChange={(value) => {
                // 清空学生选择
                form.setFieldsValue({ student_id: undefined });
                // 加载该班级的学生
                fetchStudents(value);
              }}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="student_id"
            label="学生"
            rules={[{ required: true, message: '请选择学生' }]}
          >
            <Select
              showSearch
              placeholder="请先选择班级后再选择学生"
              optionFilterProp="children"
              disabled={!form.getFieldValue('class_id')}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {students.map(student => (
                <Option key={student.id} value={student.id}>
                  {student.student_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="visit_date"
            label="家访日期"
            rules={[{ required: true, message: '请选择家访日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="visit_time"
            label="家访时间"
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="purpose"
            label="家访目的"
          >
            <Input placeholder="如：了解家庭情况、沟通学习问题..." maxLength={100} />
          </Form.Item>

          <Form.Item
            name="content"
            label="家访内容"
            rules={[{ required: true, message: '请输入家访内容' }]}
          >
            <TextArea rows={4} placeholder="请详细记录家访内容..." maxLength={1000} />
          </Form.Item>

          <Form.Item
            name="family_situation"
            label="家庭情况"
          >
            <TextArea rows={3} placeholder="家庭成员、经济情况等..." maxLength={500} />
          </Form.Item>

          <Form.Item
            name="student_performance"
            label="学生表现"
          >
            <TextArea rows={3} placeholder="学生在家的表现情况..." maxLength={500} />
          </Form.Item>

          <Form.Item
            name="parent_feedback"
            label="家长反馈"
          >
            <TextArea rows={3} placeholder="家长的意见和建议..." maxLength={500} />
          </Form.Item>

          <Form.Item
            name="follow_up_plan"
            label="后续计划"
          >
            <TextArea rows={2} placeholder="后续的跟进计划..." maxLength={500} />
          </Form.Item>

          <Form.Item
            label="附件"
          >
            <Upload {...uploadProps} listType="picture">
              <Button icon={<UploadOutlined />}>上传附件 (最多5个)</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情查看弹窗 */}
      <Modal
        title="家访详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {viewingVisit && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="学生姓名">{viewingVisit.student_name}</Descriptions.Item>
            <Descriptions.Item label="班级">{viewingVisit.class_name}</Descriptions.Item>
            <Descriptions.Item label="家访日期">{viewingVisit.visit_date}</Descriptions.Item>
            <Descriptions.Item label="家访时间">{viewingVisit.visit_time || '-'}</Descriptions.Item>
            <Descriptions.Item label="家访目的">{viewingVisit.purpose || '-'}</Descriptions.Item>
            <Descriptions.Item label="家访内容">
              <div style={{ whiteSpace: 'pre-wrap' }}>{viewingVisit.content}</div>
            </Descriptions.Item>
            <Descriptions.Item label="家庭情况">
              {viewingVisit.family_situation || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="学生表现">
              {viewingVisit.student_performance || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="家长反馈">
              {viewingVisit.parent_feedback || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="后续计划">
              {viewingVisit.follow_up_plan || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="教师">{viewingVisit.teacher_name}</Descriptions.Item>
            <Descriptions.Item label="附件">
              {viewingVisit.attachments && (() => {
                try {
                  const files = JSON.parse(viewingVisit.attachments);
                  return files.length > 0 ? (
                    <Space direction="vertical">
                      {files.map((file, index) => (
                        <a key={index} href={file.path} target="_blank" rel="noopener noreferrer">
                          {file.originalname || `附件${index + 1}`}
                        </a>
                      ))}
                    </Space>
                  ) : '-';
                } catch {
                  return '-';
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="闄勪欢棰勮">
              {renderAttachmentList(viewingVisit.attachments)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default StudentHomeVisits;






